/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
import { useRouter } from 'next/router'

import { useMemo } from 'react'

import { useAppSelector } from '~/redux/hooks'
import {
  selectCurrentSubscriptionId,
  selectSubscriptionCampaignPrice,
  selectSubscriptionIntervalType,
} from '~/redux/slices/subscription.slice'
import { isAuthenticated } from '~/auth/slices/auth.slice'
import {
  useGetStripeCurrentSubscriptionDetailsQuery,
  useGetStripePricesQuery,
} from '~/subscription/apis/subscription.api'
import { setDefaultCurrencyOfPriceToSubscriptionCurrency } from '~/subscription/util'
import { StripePrice, StripeSubscription } from '~/subscription/types/subscription.types'
import useCurrentUser from '~/hooks/useCurrentUser'
import { withoutKnowItPrices } from '~/subscription/utils/withoutKnowItPrices'

import useSubscriptionToggle from './useSubscriptionToggle'

export default function useStripePrice(
  filterBy: 'interval' | 'packageType' = 'interval',
  coupon?: string,
): {
  freePrice: StripePrice
  recommendedPrice?: StripePrice
  allPricesData: StripePrice[]
  currentSubscriptionPrice?: StripePrice
  currentSubscription?: StripeSubscription
  arePricesSettled: boolean
} {
  const router = useRouter()
  const { subscriptionType } = useSubscriptionToggle()
  const subscriptionIntervalType = useAppSelector(selectSubscriptionIntervalType)
  // getting current subscription details
  const authenticated = useAppSelector(isAuthenticated)
  const user = useCurrentUser()
  const currentSubscriptionId =
    useAppSelector(selectCurrentSubscriptionId) || user?.stripeSubscription[0]?.subscriptionId
  const { data: currSubscription, isLoading } = useGetStripeCurrentSubscriptionDetailsQuery(
    { subscriptionId: currentSubscriptionId },
    { skip: !router.isReady || !currentSubscriptionId || !authenticated },
  )
  const currentSubscription = currSubscription?.subscription
  const currSubscriptionPrice = setDefaultCurrencyOfPriceToSubscriptionCurrency(
    currSubscription?.subscription.items.data[0]?.price,
    currentSubscription,
  )

  const campaignPriceId = useAppSelector(selectSubscriptionCampaignPrice) || (router.query.price as string)

  const {
    data: pricesData,
    isSuccess: pricesSucceeded,
    isError: pricesErrored,
  } = useGetStripePricesQuery(
    {
      currency: currentSubscription?.currency,
      priceIds: campaignPriceId ? [campaignPriceId] : undefined,
      excludeDnb: true,
    },
    { skip: !router.isReady || isLoading },
  )

  const allPricesData = withoutKnowItPrices(pricesData)

  const priceDataByType = useMemo(() => {
    const geolocationPrices = allPricesData?.stripeGeoLocationPricesWithCouponDetails || []

    let filter = ({ price }: any): boolean => price.recurring.interval === subscriptionIntervalType

    if (filterBy === 'packageType') {
      if (subscriptionType === 'family') {
        filter = ({ price }: any) => price?.metadata?.subscriptionType === subscriptionType
      } else {
        filter = ({ price }: any) => !price?.metadata?.subscriptionType
      }
    }

    const filteredPrices = geolocationPrices.filter(filter)

    return filteredPrices
  }, [subscriptionIntervalType, allPricesData?.stripeGeoLocationPricesWithCouponDetails, filterBy, subscriptionType])

  return {
    freePrice: allPricesData?.freePrice,
    recommendedPrice: setDefaultCurrencyOfPriceToSubscriptionCurrency(priceDataByType?.[0]?.price, currentSubscription),
    allPricesData: priceDataByType?.map((priceData: any) => {
      return {
        ...setDefaultCurrencyOfPriceToSubscriptionCurrency(priceData.price, currentSubscription),
        coupon: { ...priceData.couponDetails },
      }
    }),
    currentSubscriptionPrice: currSubscriptionPrice,
    currentSubscription,
    // Answered either way — lets callers tell "still loading" from "came back with nothing usable"
    arePricesSettled: pricesSucceeded || pricesErrored,
  }
}
